import React, { FC, useContext } from 'react'
import { useTheme } from 'styled-components'
import LoadingState from 'modules/shares/components/LoadingState'
import { useGetPagePresentationFileQuery } from '@sketch/gql-types'
import { ShareVersionContext } from 'modules/versioning'
import { ErrorMessage } from '@sketch/components'
import config from '@sketch/env-config'
import {
  DefaultFeatureFlags as SketchWebDefaultFeatureFlags,
  useStatus,
  CanvasRendererReact,
  WebRendererMode,
} from '@sketch-hq/sketch-web-renderer'
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-processing-64-svg'
import { getRenderStatus } from 'utils/renderStatus'
import { useDevToolsSetting } from 'modules/devtools/settings'
import {
  StyledPageCanvas,
  StyledWebRendererStatusContainer,
  PageCanvasWrapper,
} from './PageCanvas.styles'
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'
import { MAX_ZOOM, MIN_ZOOM } from '../../constants'
import {
  usePrFileStatusChangesSubscriptionIfNeeded,
  useSafariGPUWarning,
  useSketchWebMetrics,
  useVersionMismatchWarning,
  useInitialCameraPosition,
  useSaveLastCameraPositionInLocalStorage,
  usePageCanvasAnnotations,
} from '../../hooks'
import { PageCanvasStatus } from '../PageCanvasStatus'
import CanvasContextMenu, { useCanvasContextMenu } from '../CanvasContextMenu'
import { parseRGBA } from '../../utils'

import PageOriginContent from '../PageOriginContent'
import { ArtboardsInfoOverlay } from '../ArtboardsInfoOverlay'

import { PageAnnotationsOverlay } from 'modules/annotations/components'

const IS_CYPRESS = 'Cypress' in window

const CAN_DEBUG =
  process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'

// Certain web renderer features can be enabled/disabled using feature flags
const SKETCH_WEB_FEATURE_FLAGS: typeof SketchWebDefaultFeatureFlags = {
  ...SketchWebDefaultFeatureFlags,

  // The dirty rects rendering is a new feature that
  // we're currently testing, but want to keep disabled in prod
  useDirtyRectsRendering: false,
}

interface PageCanvasProps {
  permanentPageIdentifier: string
}

export const PageCanvas: FC<PageCanvasProps> = ({
  permanentPageIdentifier: pageUUID,
}) => {
  const shareVersionContext = useContext(ShareVersionContext)!
  const { versionShortId, share } = shareVersionContext
  const [devToolsMode] = useDevToolsSetting('webRenderer.mode')
  const [devToolsShowTilesBorders] = useDevToolsSetting(
    'webRenderer.showTilesBorders'
  )
  const theme = useTheme()

  const {
    contextMenuPos,
    contextMenuMetaData,
    canvasContainerRef,
    handleContextMenu,
    handleCloseContextMenu,
  } = useCanvasContextMenu()

  useVersionMismatchWarning()
  useSafariGPUWarning()

  const { initialPan, initialZoom } = useInitialCameraPosition({
    pageUUID,
  })

  useSaveLastCameraPositionInLocalStorage({ pageUUID })

  const status = useStatus()
  const shareID = share.identifier

  const {
    activeAnnotation,
    isPlacingDraftAnnotation,
    ...pointerEvents
  } = usePageCanvasAnnotations(shareID, pageUUID)

  const documentRenderStatus = !shareVersionContext.loading
    ? getRenderStatus(
        shareVersionContext.currentVersion.document?.renderStatus ?? null
      )
    : null

  const prFileQuery = useGetPagePresentationFileQuery({
    variables: {
      pageUUID,
      share: shareID,
      version: versionShortId,
    },
    // Hold the PR file request until the document is marked as
    // ready on the backend.
    skip: documentRenderStatus !== 'ready',
  })

  const presentationFile =
    prFileQuery.data?.share?.version?.document?.page?.presentationFile
  const pageIdentifier =
    prFileQuery.data?.share?.version?.document?.page?.identifier

  usePrFileStatusChangesSubscriptionIfNeeded(
    documentRenderStatus,
    presentationFile,
    pageIdentifier
  )

  const filePath = presentationFile?.downloadUrl
  const imagesURLFormat = `${config.web_renderer_image_domain}/presentation/p/${pageUUID}/i/:imageId?token=${presentationFile?.imageToken}`

  useSketchWebMetrics({ shareID, pageUUID, versionShortId, filePath })

  if (shareVersionContext.loading || prFileQuery.loading) {
    return <LoadingState />
  }

  if (
    documentRenderStatus !== 'ready' ||
    presentationFile?.status === 'PENDING'
  ) {
    return (
      <ErrorMessage
        title="Rendering Canvas…"
        description="The canvas is still rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    )
  }

  /**
   * By this point filePath shouldn't ever be undefined, but we narrow the type
   * to a defined string with this error case just to be sure.
   */
  if (!filePath) {
    return (
      <PageCanvasError
        description={
          <>
            Something went wrong while loading the canvas. Please reload — or
            try again later.
          </>
        }
        showReloadCanvasAction
        showViewAsArtboardsAction
      />
    )
  }

  /**
   * This error condition is a relic from the early MVP of the canvas that still
   * used the legacy JSON format. As such only internal Sketch users that may
   * still have a JSON PR file associated with their Sketch document will ever
   * see it. Safe to remove in future.
   */
  if (filePath.includes('.json')) {
    return (
      <PageCanvasError
        description={
          <>
            Please save a new document version in Sketch and reload to see this
            page as a canvas.
          </>
        }
        showReloadCanvasAction
        showViewAsArtboardsAction
      />
    )
  }

  if (prFileQuery.error) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the page data. You can find more
            information on our{' '}
            <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
        showViewAsArtboardsAction
      />
    )
  }

  const mode = IS_CYPRESS
    ? WebRendererMode.debug
    : CAN_DEBUG
    ? WebRendererMode[devToolsMode]
    : WebRendererMode.release

  const showTilesBorders = CAN_DEBUG ? devToolsShowTilesBorders : false

  /**
   * Only enable this flag in Cypress.
   * @see https://github.com/sketch-hq/Cloud/issues/11052
   * @see https://docs.cypress.io/faq/questions/using-cypress-faq#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
   */
  const preserveDrawingBuffer = IS_CYPRESS

  return (
    <PageCanvasWrapper>
      <StyledPageCanvas ref={canvasContainerRef}>
        <CanvasRendererReact
          initialPan={initialPan}
          initialZoom={initialZoom}
          filePath={filePath}
          imagesURLFormat={imagesURLFormat}
          mode={mode}
          showTilesBorders={showTilesBorders}
          preserveDrawingBuffer={preserveDrawingBuffer}
          locateFile={`${process.env.WEB_RENDERER_WASM_MODULES_FOLDER}/{file}`}
          minimumZoomLevel={MIN_ZOOM}
          maximumZoomLevel={MAX_ZOOM}
          backgroundColor={parseRGBA(theme.colors.background.secondary.B)}
          cursor={isPlacingDraftAnnotation ? 'copy' : undefined}
          containerProps={{
            onContextMenu: handleContextMenu,
            ...pointerEvents,
          }}
          featureFlags={SKETCH_WEB_FEATURE_FLAGS}
        >
          <PageOriginContent>
            <ArtboardsInfoOverlay
              disablePointerCursor={isPlacingDraftAnnotation}
            />
            <PageAnnotationsOverlay
              shareIdentifier={share.identifier}
              permanentPageId={pageUUID}
              activeAnnotationIdentifier={activeAnnotation}
              parentWrapper={canvasContainerRef.current || undefined}
            />
          </PageOriginContent>
        </CanvasRendererReact>

        <CanvasContextMenu
          closeTooltip={handleCloseContextMenu}
          contextMenuPos={contextMenuPos}
          contextMenuMetaData={contextMenuMetaData}
        />
        {status?.type !== 'READY' && (
          <StyledWebRendererStatusContainer data-testid="loading-canvas-overlay">
            <PageCanvasStatus status={status} />
          </StyledWebRendererStatusContainer>
        )}
      </StyledPageCanvas>
    </PageCanvasWrapper>
  )
}
